import { budgetAlert, BudgetFeedback } from "./BudgetAlert";
import React, { useState, useEffect } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Modal from "react-modal";

import Grid from "components/Grid/GridContainer.js";
import Col from "components/Grid/GridItem.js";
import Card from "components/Card/Card.js";
import CardHeader from "components/Card/CardHeader.js";
import CardBody from "components/Card/CardBody.js";
import Button from "components/CustomButtons/Button.js";

import { BudgetService } from "_services/budget.service";
import Datetime from "react-datetime";
import "react-datetime/css/react-datetime.css";
import moment from "moment";
import "moment/locale/th";

moment.locale("th");

const styles = {
    cardTitleWhite: { color: "#FFFFFF", margin: 0, fontWeight: "300", fontFamily: '"Sarabun", sans-serif' },
    modalOverlay: { backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1050 },
    modalContent: { top: '50%', left: '50%', right: 'auto', bottom: 'auto', marginRight: '-50%', transform: 'translate(-50%, -50%)', width: '500px', maxWidth: '90vw', maxHeight: '90vh', overflowY: 'auto', padding: '20px', borderRadius: '8px' }
};
const useStyles = makeStyles(styles);

const newRound = () => ({
    CalYear: moment().add(543, 'years').format('YYYY'),
    RoundName: "ขอครั้งที่ 1",
    StartDate: moment().format('YYYY-MM-DD'),
    EndDate: moment().add(30, 'days').format('YYYY-MM-DD'),
    CommitteeReviewDate: moment().add(40, 'days').format('YYYY-MM-DD'),
    IsActive: true
});

const BudgetRoundList = () => {
    const classes = useStyles();

    const [rounds, setRounds] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isPlan, setIsPlan] = useState(false);
    const [editingRoundKey, setEditingRoundKey] = useState(null);
    const [busyRoundKey, setBusyRoundKey] = useState(null);
    const [error, setError] = useState("");
    const [formData, setFormData] = useState(newRound);
    const isBusy = isSaving || busyRoundKey !== null;

    useEffect(() => {
        Modal.setAppElement('body');
        fetchRounds();
    }, []);

    const fetchRounds = async () => {
        setIsLoading(true);
        try {
            const res = await BudgetService.getAllRounds();
            if (!res.data.success) throw new Error(res.data.message || "ไม่สามารถโหลดรอบงบประมาณได้");
            setRounds(res.data.data || []);
            setIsPlan(res.data.isPlan === true);
            setError("");
        } catch (error) {
            setIsPlan(false);
            setError(error.response?.data?.message || error.message);
        } finally {
            setIsLoading(false);
        }
    };

    const handleOpenRound = (round = null) => {
        if (!isPlan || isBusy) return;
        setEditingRoundKey(round ? round.roundKey : null);
        setFormData(round ? {
            CalYear: round.calYear,
            RoundName: round.roundName,
            StartDate: moment(round.startDate).format('YYYY-MM-DD'),
            EndDate: moment(round.endDate).format('YYYY-MM-DD'),
            CommitteeReviewDate: round.committeeReviewDate ? moment(round.committeeReviewDate).format('YYYY-MM-DD') : "",
            IsActive: round.isActive
        } : newRound());
        setIsModalOpen(true);
    };

    const handleSaveRound = async (e) => {
        e.preventDefault();
        if (!isPlan || isBusy) return;
        if (!formData.RoundName.trim()) return budgetAlert.warning("กรุณาระบุชื่อรอบ");
        if (!moment(formData.StartDate, 'YYYY-MM-DD', true).isValid() ||
            !moment(formData.EndDate, 'YYYY-MM-DD', true).isValid()) {
            return budgetAlert.warning("กรุณาระบุวันที่เริ่มและวันที่สิ้นสุดให้ครบถ้วน");
        }
        if (moment(formData.EndDate).isBefore(moment(formData.StartDate), 'day')) {
            return budgetAlert.warning("วันที่สิ้นสุดต้องไม่น้อยกว่าวันที่เริ่มเปิด");
        }
        setIsSaving(true);
        try {
            const payload = {
                ...formData,
                CalYear: parseInt(formData.CalYear, 10),
                RoundName: formData.RoundName.trim(),
                CommitteeReviewDate: formData.CommitteeReviewDate || null
            };
            const res = editingRoundKey
                ? await BudgetService.updateRound(editingRoundKey, payload)
                : await BudgetService.createRound(payload);
            if (!res.data.success) throw new Error(res.data.message || "ไม่สามารถบันทึกรอบงบประมาณได้");
            await budgetAlert.success(editingRoundKey ? "แก้ไขรอบงบประมาณสำเร็จ" : "เปิดรอบงบประมาณสำเร็จ");
            setIsModalOpen(false);
            await fetchRounds();
        } catch (error) {
            budgetAlert.error("เกิดข้อผิดพลาด: " + (error.response?.data?.message || error.message));
        } finally {
            setIsSaving(false);
        }
    };

    const handleDeactivateRound = async (round) => {
        if (!isPlan || isBusy || !round.isActive) return;
        if (!await budgetAlert.confirm(`ยืนยันปิดใช้งานรอบ "${round.roundName}"?`)) return;
        setBusyRoundKey(round.roundKey);
        try {
            const res = await BudgetService.toggleRoundStatus(round.roundKey, false);
            if (!res.data.success) throw new Error(res.data.message || "ไม่สามารถปิดใช้งานรอบได้");
            await fetchRounds();
        } catch (error) {
            budgetAlert.error("เกิดข้อผิดพลาด: " + (error.response?.data?.message || error.message));
        } finally {
            setBusyRoundKey(null);
        }
    };

    const renderDateField = (name, label) => (
        <div className="form-group mt-3">
            <label>{label}</label>
            <Datetime
                value={formData[name] ? moment(formData[name]) : ""}
                renderYear={(props, year) => <td {...props}>{year + 543}</td>}
                inputProps={{
                    placeholder: "วว/ดด/ปปปป (พ.ศ.)",
                    readOnly: true,
                    disabled: isSaving,
                    'aria-label': label,
                    style: { cursor: "pointer", backgroundColor: "#ffffff" },
                    className: "form-control",
                    value: formData[name] ? moment(formData[name]).format("D MMMM BBBB") : ""
                }}
                closeOnSelect={true}
                closeOnTab={true}
                dateFormat="D MMMM BBBB"
                timeFormat={false}
                onChange={(val) => {
                    if (moment.isMoment(val) && val.isValid()) {
                        setFormData(prev => ({ ...prev, [name]: val.format("YYYY-MM-DD") }));
                    }
                }}
            />
            {name === "CommitteeReviewDate" && formData[name] && (
                <button type="button" className="btn btn-link btn-sm" disabled={isSaving}
                    onClick={() => setFormData(prev => ({ ...prev, [name]: "" }))}>
                    ล้างวันพิจารณา
                </button>
            )}
        </div>
    );

    return (
        <div>
            <Grid>
                <Col xs={12}>
                    <Card>
                        <CardHeader color="info" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <h4 className={classes.cardTitleWhite}>จัดการรอบการของบประมาณ</h4>
                            {isPlan && (
                                <Button color="success" size="sm" disabled={isLoading || isBusy} onClick={() => handleOpenRound()}>
                                    <i className="fas fa-plus"></i> เปิดรอบใหม่
                                </Button>
                            )}
                        </CardHeader>
                        <CardBody>
                            <BudgetFeedback message={error} />
                            {!isLoading && !error && !isPlan && (
                                <p className="text-muted">คุณสามารถดูรอบงบประมาณได้ การเพิ่ม แก้ไข และปิดใช้งานสงวนสิทธิ์สำหรับงานแผนงานและงานคอมพิวเตอร์</p>
                            )}
                            {isLoading ? (
                                <p className="text-center p-4">กำลังโหลดข้อมูลรอบงบประมาณ...</p>
                            ) : (
                                <table className="table table-bordered table-striped mt-3" style={{ fontSize: "14px" }}>
                                    <thead className="thead-light">
                                        <tr>
                                            <th width="50">#</th>
                                            <th>ปีงบประมาณ</th>
                                            <th>ชื่อรอบ</th>
                                            <th>วันที่เริ่ม</th>
                                            <th>วันที่สิ้นสุด</th>
                                            <th>วันพิจารณากรรมการ</th>
                                            <th className="text-center">สถานะ</th>
                                            {isPlan && <th className="text-center">จัดการ</th>}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {rounds.length === 0 && (
                                            <tr><td colSpan={isPlan ? 8 : 7} className="text-center text-muted p-4">ยังไม่มีรอบงบประมาณ</td></tr>
                                        )}
                                        {rounds.map((round, idx) => {
                                            const now = moment();
                                            const isCurrentlyOpen = round.isActive && now.isBetween(moment(round.startDate), moment(round.endDate), 'day', '[]');
                                            return (
                                                <tr key={round.roundKey}>
                                                    <td>{idx + 1}</td>
                                                    <td>{round.calYear}</td>
                                                    <td><strong>{round.roundName}</strong></td>
                                                    <td>{moment(round.startDate).format('D MMMM YYYY')}</td>
                                                    <td>{moment(round.endDate).format('D MMMM YYYY')}</td>
                                                    <td>{round.committeeReviewDate ? moment(round.committeeReviewDate).format('D MMMM YYYY') : '-'}</td>
                                                    <td className="text-center">
                                                        {!round.isActive ? (
                                                            <span className="badge badge-danger p-2">ปิดใช้งาน</span>
                                                        ) : isCurrentlyOpen ? (
                                                            <span className="badge badge-success p-2">กำลังเปิดรับ</span>
                                                        ) : (
                                                            <span className="badge badge-secondary p-2">{now.isBefore(moment(round.startDate), 'day') ? "ยังไม่ถึงกำหนด" : "หมดเขต"}</span>
                                                        )}
                                                    </td>
                                                    {isPlan && (
                                                        <td className="text-center">
                                                            <Button color="warning" size="sm" disabled={isBusy} onClick={() => handleOpenRound(round)}>
                                                                แก้ไข
                                                            </Button>
                                                            {round.isActive && (
                                                                <Button color="danger" size="sm" disabled={isBusy} onClick={() => handleDeactivateRound(round)}>
                                                                    {busyRoundKey === round.roundKey ? "กำลังปิดใช้งาน..." : "ปิดใช้งาน"}
                                                                </Button>
                                                            )}
                                                        </td>
                                                    )}
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            )}
                        </CardBody>
                    </Card>
                </Col>
            </Grid>

            <Modal isOpen={isModalOpen && isPlan} onRequestClose={() => !isSaving && setIsModalOpen(false)}
                contentLabel={editingRoundKey ? "แก้ไขรอบงบประมาณ" : "เปิดรอบงบประมาณใหม่"}
                style={{ overlay: styles.modalOverlay, content: styles.modalContent }}>
                <h4>{editingRoundKey ? "แก้ไขรอบการของบประมาณ" : "เปิดรอบการของบประมาณใหม่"}</h4>
                <form onSubmit={handleSaveRound}>
                    <div className="form-group mt-3">
                        <label>ปีงบประมาณ (พ.ศ.)</label>
                        <input type="number" aria-label="ปีงบประมาณ (พ.ศ.)" className="form-control" min="1" max="9999" disabled={isSaving} value={formData.CalYear} onChange={(e) => setFormData({ ...formData, CalYear: e.target.value })} required />
                    </div>
                    <div className="form-group mt-3">
                        <label>ชื่อรอบ</label>
                        <input type="text" aria-label="ชื่อรอบ" className="form-control" maxLength={200} disabled={isSaving} value={formData.RoundName} onChange={(e) => setFormData({ ...formData, RoundName: e.target.value })} required />
                    </div>
                    <div className="row mt-3">
                        <div className="col-md-6">
                            {renderDateField("StartDate", "วันที่เริ่มเปิด (พ.ศ.)")}
                        </div>
                        <div className="col-md-6">
                            {renderDateField("EndDate", "วันที่ปิดระบบ (พ.ศ.)")}
                        </div>
                    </div>
                    {renderDateField("CommitteeReviewDate", "วันพิจารณากรรมการ (พ.ศ.)")}
                    {editingRoundKey && (
                        <label className="mt-3">
                            <input type="checkbox" checked={formData.IsActive} disabled={isSaving}
                                onChange={(e) => setFormData({ ...formData, IsActive: e.target.checked })} /> เปิดใช้งานรอบงบประมาณ
                        </label>
                    )}
                    <div className="mt-4 text-right">
                        <Button color="transparent" type="button" disabled={isSaving} onClick={() => setIsModalOpen(false)}>ยกเลิก</Button>
                        <Button color="primary" type="submit" disabled={isSaving}>
                            {isSaving ? "กำลังบันทึก..." : editingRoundKey ? "บันทึกการแก้ไข" : "บันทึกเปิดรอบ"}
                        </Button>
                    </div>
                </form>
            </Modal>
        </div>
    );
};

export default BudgetRoundList;
